import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Edit, Trash2, MapPin, Building } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyLocationManagement({ user }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [locationNotes, setLocationNotes] = useState("");
  const [locationLatitude, setLocationLatitude] = useState("");
  const [locationLongitude, setLocationLongitude] = useState("");
  const [checkinRadius, setCheckinRadius] = useState(200);
  const [gettingLocation, setGettingLocation] = useState(false);

  const isAdmin = ["convenor", "captain"].includes(user?.role);

  useEffect(() => {
    if (isAdmin) {
      fetchLocations();
    }
  }, [isAdmin]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/academy/locations`, { withCredentials: true });
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingLocation(null);
    setLocationName("");
    setLocationAddress("");
    setLocationNotes("");
    setLocationLatitude("");
    setLocationLongitude("");
    setCheckinRadius(200);
    setShowDialog(true);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setLocationName(location.name);
    setLocationAddress(location.address || "");
    setLocationNotes(location.notes || "");
    setLocationLatitude(location.latitude || "");
    setLocationLongitude(location.longitude || "");
    setCheckinRadius(location.checkin_radius_meters || 200);
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!locationName.trim()) {
      toast.error("Please enter a location name");
      return;
    }

    try {
      const payload = {
        name: locationName.trim(),
        address: locationAddress.trim(),
        notes: locationNotes.trim(),
        latitude: locationLatitude ? parseFloat(locationLatitude) : null,
        longitude: locationLongitude ? parseFloat(locationLongitude) : null,
        checkin_radius_meters: checkinRadius
      };

      if (editingLocation) {
        await axios.put(`${API}/academy/locations/${editingLocation.id}`, payload, { withCredentials: true });
        toast.success("Location updated successfully");
      } else {
        await axios.post(`${API}/academy/locations`, payload, { withCredentials: true });
        toast.success("Location created successfully");
      }

      setShowDialog(false);
      fetchLocations();
    } catch (error) {
      console.error("Error saving location:", error);
      toast.error(error.response?.data?.detail || "Failed to save location");
    }
  };

  // Get current GPS location for the venue
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationLatitude(position.coords.latitude.toFixed(6));
        setLocationLongitude(position.coords.longitude.toFixed(6));
        toast.success("GPS coordinates captured!");
        setGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Failed to get location. Please enable location services.");
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleToggleActive = async (location) => {
    try {
      await axios.put(
        `${API}/academy/locations/${location.id}`,
        { is_active: !location.is_active },
        { withCredentials: true }
      );
      toast.success(location.is_active ? "Location deactivated" : "Location activated");
      fetchLocations();
    } catch (error) {
      console.error("Error toggling location:", error);
      toast.error(error.response?.data?.detail || "Failed to update location");
    }
  };

  const handleDelete = async (location) => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/academy/locations/${location.id}`, { withCredentials: true });
      toast.success("Location deleted successfully");
      fetchLocations();
    } catch (error) {
      console.error("Error deleting location:", error);
      toast.error(error.response?.data?.detail || "Failed to delete location");
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-6">
        <Card className="max-w-2xl mx-auto p-8 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-slate-400">Only administrators can manage locations.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-white">Training Locations</h1>
          </div>
          <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        </div>

        {/* Info Card */}
        <Card className="p-4 mb-6 bg-blue-500/10 border-blue-500/30">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="text-sm text-blue-400">
              <p className="font-medium mb-1">Manage Training Locations</p>
              <p>Add, edit, or deactivate training locations. These locations will appear in the session creation dropdown.</p>
              <p className="mt-1 text-xs text-blue-400">
                Note: Locations used by sessions cannot be deleted, but can be deactivated to hide from new sessions.
              </p>
            </div>
          </div>
        </Card>

        {/* Locations List */}
        <div className="space-y-3">
          {locations.length === 0 ? (
            <Card className="p-8 text-center">
              <Building className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Locations Yet</h3>
              <p className="text-slate-400 mb-4">Add your first training location to get started.</p>
              <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </Card>
          ) : (
            locations.map((location) => (
              <Card 
                key={location.id} 
                className={`p-4 ${!location.is_active ? 'bg-slate-800/50 opacity-70' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${location.is_active ? 'bg-blue-500/20' : 'bg-slate-700'}`}>
                      <MapPin className={`w-5 h-5 ${location.is_active ? 'text-blue-400' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-white">{location.name}</h3>
                        {!location.is_active && (
                          <span className="px-2 py-0.5 bg-slate-700 text-slate-400 text-xs rounded">
                            Inactive
                          </span>
                        )}
                        {location.latitude && location.longitude && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> GPS Set
                          </span>
                        )}
                      </div>
                      {location.address && (
                        <p className="text-sm text-slate-400 mt-1">{location.address}</p>
                      )}
                      {location.notes && (
                        <p className="text-xs text-slate-400 mt-1 italic">{location.notes}</p>
                      )}
                      {location.latitude && location.longitude && (
                        <p className="text-xs text-slate-500 mt-1">
                          📍 {location.latitude}, {location.longitude} (±{location.checkin_radius_meters || 200}m)
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Active Toggle */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Active</span>
                      <Switch
                        checked={location.is_active}
                        onCheckedChange={() => handleToggleActive(location)}
                      />
                    </div>
                    
                    {/* Edit Button */}
                    <Button
                      onClick={() => openEditDialog(location)}
                      variant="outline"
                      size="sm"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      data-testid={`edit-location-${location.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    
                    {/* Delete Button */}
                    <Button
                      onClick={() => handleDelete(location)}
                      variant="outline"
                      size="sm"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                      data-testid={`delete-location-${location.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingLocation ? "Edit Location" : "Add New Location"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Location Name *</label>
                <Input
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="e.g., Dempsey Field"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Address (Optional)</label>
                <Input
                  value={locationAddress}
                  onChange={(e) => setLocationAddress(e.target.value)}
                  placeholder="e.g., 123 Dempsey Road, Singapore"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                <Input
                  value={locationNotes}
                  onChange={(e) => setLocationNotes(e.target.value)}
                  placeholder="e.g., Main training ground, indoor facility"
                />
              </div>

              {/* GPS Coordinates Section */}
              <div className="border border-slate-700 rounded-lg p-4 bg-slate-800/50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-white flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-400" />
                      GPS Coordinates
                    </h4>
                    <p className="text-xs text-slate-400 mt-1">
                      For coach check-in verification. Go to venue and click "Get Current Location".
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {gettingLocation ? "Getting..." : "📍 Get Current Location"}
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-400">Latitude</label>
                    <Input
                      type="number"
                      step="0.000001"
                      value={locationLatitude}
                      onChange={(e) => setLocationLatitude(e.target.value)}
                      placeholder="e.g., 1.3521"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-400">Longitude</label>
                    <Input
                      type="number"
                      step="0.000001"
                      value={locationLongitude}
                      onChange={(e) => setLocationLongitude(e.target.value)}
                      placeholder="e.g., 103.8198"
                      className="text-sm"
                    />
                  </div>
                </div>
                
                <div className="mt-3">
                  <label className="block text-xs font-medium mb-1 text-slate-400">Check-in Radius (meters)</label>
                  <Input
                    type="number"
                    value={checkinRadius}
                    onChange={(e) => setCheckinRadius(parseInt(e.target.value) || 200)}
                    placeholder="200"
                    className="text-sm w-32"
                  />
                  <p className="text-xs text-slate-500 mt-1">Coach must be within this distance to verify location</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                <Button onClick={() => setShowDialog(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
                  {editingLocation ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
